import { h, Component, Fragment } from 'preact';
import { observer } from 'mobx-react';
import classnames from 'classnames';

import { Autocomplete as LibraryAutocomplete } from '@searchspring/snap-preact-components';

import { Result, PRODUCT_CARD_TYPES } from '../Result/Result';
import { Tabs } from '../Tabs/Tabs';
import { fetchLocaleField } from '../Helpers';

import './autocomplete.css';
import { Article, ARTICLE_CARD_TYPES } from '../Result/Article';
import { useEffect, useRef } from 'preact/hooks';

@observer
export class Autocomplete extends Component {
	componentDidUpdate() {
		const ssAutocompleteWrapper = document.querySelector('.ss__autocomplete__content');
		if (ssAutocompleteWrapper && !ssAutocompleteWrapper.ariaLive) {
			ssAutocompleteWrapper.ariaLive = 'polite';
			ssAutocompleteWrapper.role = 'status';
		}
	}

	render() {
		const tabs = this.props.tabs;
		const controller = tabs?.active?.controller || this.props.controller;
		const lastSearch = useRef();

		const autocompleteStrings = window.Resources?.searchspring?.typeahead ?? {};

		const totalResults = controller.store.pagination.totalResults;
		const currentSearch = document.querySelector('.search__input').value;

		useEffect(() => {
			if (totalResults === 0 && lastSearch.current === tabs?.active?.id) {
				const nextTab = tabs.config.tabs.find((tab) => tab.id !== tabs?.active?.id);
				lastSearch.current = nextTab.id;
				tabs.switch(nextTab.id);
			}
		}, [controller.store.pagination]);

		useEffect(() => {
			lastSearch.current = tabs?.active?.id;
		}, [currentSearch]);

		const resultsTitle = controller.store.state.input ? autocompleteStrings.product_search_tab : autocompleteStrings.popularProducts;

		return (
			<LibraryAutocomplete
				controller={controller}
				input={controller.config.selector}
				hideFacets
				hideBanners
				termsTitle={autocompleteStrings.suggestions}
				trendingTitle={autocompleteStrings.topSearches}
				contentTitle={resultsTitle}
				resultsSlot={<AutocompleteResults tabs={this.props.tabs} controller={controller} />}
				noResultsSlot={<AutocompleteNoResults tabs={this.props.tabs} controller={controller} />}
				termsSlot={<AutocompleteTerms />}
			/>
		);
	}
}

// For reference, you can see where this slot component gets used by the library Autocomplete component here:
// https://github.com/searchspring/snap/blob/main/packages/snap-preact-components/src/components/Organisms/Autocomplete/Autocomplete.tsx#L503
const AutocompleteResults = ({ results, contentTitle, controller, tabs }) => {
	const numberOfResultsToShow = 4; // magic number - could later be shopify setting driven
	const resultsToShow = results.slice(0, numberOfResultsToShow);
	const { state } = controller.store;

	//Prevents "delay" on first load in
	//Only show once useEffect is ready
	const parentContainerDesktop = document.querySelector('.header__search-desktop .ss__autocomplete--target');
	useEffect(() => {
		if (parentContainerDesktop) {
			parentContainerDesktop.style.opacity = 1;
		}
	});

	//Prevents "delay" on first load in (mobile)
	const parentContainerMobile = document.querySelector('.header__search-mobile .ss__autocomplete--target');
	useEffect(() => {
		if (parentContainerMobile) {
			parentContainerMobile.style.opacity = 1;
		}
	});

	const autocompleteStrings = window.Resources?.searchspring?.typeahead ?? {};

	function TypeAheadCustomCard({ result }) {
		const rechargeBundlesMetafield = fetchLocaleField(result, 'mfield_rc_bundles_layout_template');

		if (controller.store.config?.id == 'ac-articles' || controller.store.config?.id == 'ac-articles-mobile') {
			return <Article result={result} cardType={ARTICLE_CARD_TYPES.TYPE_AHEAD_BLOG_CARD} blogRec={false} />;
		}

		if (rechargeBundlesMetafield == 'one-page') {
			return <Result cardType={PRODUCT_CARD_TYPES.TYPE_AHEAD_FIXED_BUNDLE_PRODUCT_CARD} result={result} />;
		}

		if (rechargeBundlesMetafield == 'multi-step') {
			return <Result cardType={PRODUCT_CARD_TYPES.TYPE_AHEAD_CUSTOM_BUNDLE_PRODUCT_CARD} result={result} />;
		}

		return <Result result={result} cardType={PRODUCT_CARD_TYPES.TYPE_AHEAD_STANDARD_PRODUCT_CARD} />;
	}

	return (
		<>
			<div>
				<Tabs tabs={tabs} controller={controller} inAc={true} />
			</div>
			<div className="ss__autocomplete__results-wrap">
				<div className="ss__autocomplete__results">
					{resultsToShow.map((result) => (
						<div className="ss__autocomplete__result">
							<TypeAheadCustomCard result={result} />
						</div>
					))}
				</div>
				{!!state.input && results.length > 0 && (
					<div className="ss__autocomplete__view-all">
						<a className="ss__autocomplete__view-all__link link button button--secondary-black" href={state.url.href}>
							<span>{autocompleteStrings.viewAllResults}</span>
						</a>
					</div>
				)}
			</div>
		</>
	);
};

const AutocompleteNoResults = ({ controller, tabs }) => {
	return (
		<>
			<div>
				<Tabs tabs={tabs} controller={controller} inAc={true} />
			</div>
			<p className="ss__autocomplete__no-result">{window.Resources?.searchspring?.typeahead?.noResultsAutocomplete}</p>
		</>
	);
};

// For reference, you can see where this slot component gets used by the library Autocomplete component here:
// https://github.com/searchspring/snap/blob/main/packages/snap-preact-components/src/components/Organisms/Autocomplete/Autocomplete.tsx#L402
const AutocompleteTerms = (props) => {
	const { terms, trending, termsTitle, trendingTitle, showTrending, valueProps, emIfy, onTermClick, controller } = props;
	const { state } = controller.store;
	return (
		<>
			{terms.length > 0 ? (
				<>
					<div className="ss__autocomplete__inner-wrapper">
						{termsTitle ? (
							<div className="ss__autocomplete__title ss__autocomplete__title--terms">
								<h5 className="ss__autocomplete__terms-heading">{termsTitle}</h5>
							</div>
						) : null}
						<div className="ss__autocomplete__terms__options">
							{terms.map((term) => (
								<div
									className={classnames('ss__autocomplete__terms__option', 'CQL-p3', {
										'ss__autocomplete__terms__option--active': term.active,
									})}
								>
									<a onClick={(e) => onTermClick && onTermClick(e)} href={term.url.href} {...valueProps} onFocus={() => term.preview()}>
										{emIfy(term.value, state.input)}
									</a>
								</div>
							))}
						</div>
					</div>
				</>
			) : null}

			{showTrending ? (
				<div className="ss__autocomplete__inner-wrapper">
					{trendingTitle ? (
						<div className="ss__autocomplete__title ss__autocomplete__title--trending">
							<h5 className="ss__autocomplete__terms--heading">{trendingTitle}</h5>
						</div>
					) : null}
					<div className="ss__autocomplete__terms__options">
						{trending.map((term) => (
							<div
								className={classnames('ss__autocomplete__terms__option', {
									'ss__autocomplete__terms__option--active': term.active,
								})}
							>
								<a onClick={(e) => onTermClick && onTermClick(e)} href={term.url.href} {...valueProps} onFocus={() => term.preview()}>
									{emIfy(term.value, state.input)}
								</a>
							</div>
						))}
					</div>
				</div>
			) : null}
		</>
	);
};
